import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import DocumentToolbar from './DocumentToolbar';
import COMPANY_INFO from '../../config/companyInfo';
import api from '../../services/api';

function useShipmentType() {
  const { pathname } = useLocation();
  if (pathname.includes('/air-import/')) return 'air-import';
  if (pathname.includes('/ocean-import/')) return 'ocean-import';
  if (pathname.includes('/ocean-export/')) return 'ocean-export';
  if (pathname.includes('/misc/')) return 'misc';
  return 'truck';
}

function normalizeShipment(raw, type) {
  if (type === 'air-import') {
    const h = (raw.hawbs && raw.hawbs[0]) || {};
    return {
      file_no: raw.file_no, post_date: raw.post_date,
      mbl_no: raw.mawb_no || '', hbl_no: h.hawb_no || '',
      vessel_flight_no: raw.flight_no || '',
      customer: h.customer || '', consignee: h.consignee || '',
      shipper: h.shipper || '', bill_to: h.bill_to || '',
      port_of_loading: raw.departure || '', port_of_discharge: raw.destination || '',
      etd: raw.departure_date_time || '', eta: raw.arrival_date_time || '',
      final_destination: h.final_destination || raw.destination || '',
      final_eta: h.final_eta || '',
      package_count: raw.package_count || h.package_count || '',
      package_type: raw.pkg_type || h.pkg_type || '',
      weight_kg: raw.gross_weight_kg || h.gross_weight_kg || '',
      measurement_cbm: raw.measurement_cbm || h.measurement_cbm || '',
      memo: raw.memo || h.memo || '', created_at: raw.created_at,
    };
  }
  return raw;
}

const ArrivalNotice = () => {
  const { id } = useParams();
  const shipmentType = useShipmentType();
  const contentRef = useRef(null);

  const [shipment, setShipment] = useState(null);
  const [containers, setContainers] = useState([]);
  const [consigneeInfo, setConsigneeInfo] = useState(null);
  const [shipperInfo, setShipperInfo] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchData(); }, [id, shipmentType]);

  const fetchTP = async (name) => {
    if (!name) return null;
    try {
      const r = await api.get(`/trade-partners/search?q=${encodeURIComponent(name)}`);
      if (Array.isArray(r.data) && r.data.length > 0) return r.data[0];
    } catch {}
    return null;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/${shipmentType}/${id}`);
      const s = normalizeShipment(res.data, shipmentType);
      setShipment(s);

      if (shipmentType === 'truck' || shipmentType.includes('ocean')) {
        try {
          const contRes = await api.get(`/containers/shipment/${id}`);
          setContainers(Array.isArray(contRes.data) ? contRes.data : []);
        } catch { setContainers([]); }
      }

      const [cons, ship, cust] = await Promise.all([
        fetchTP(s.consignee), fetchTP(s.shipper), fetchTP(s.customer),
      ]);
      setConsigneeInfo(cons); setShipperInfo(ship); setCustomerInfo(cust);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  if (error) return <div style={{ padding: '20px', color: 'red' }}>Error: {error}</div>;
  if (!shipment) return <div style={{ padding: '20px' }}>No shipment data found</div>;

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getDate().toString().padStart(2,'0')}/${d.getFullYear()}`;
  };
  const getTPAddress = (info) => {
    if (!info) return '';
    return [info.address, info.city, info.state].filter(Boolean).join(', ');
  };
  const currentDate = formatDate(new Date());
  const isAir = shipmentType === 'air-import';

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', padding: '20px' }}>
      <DocumentToolbar title="Arrival Notice" contentRef={contentRef} pdfUrl={`/pdf/shipment/${shipmentType}/${id}/arrival-notice`} onClose={() => window.close()} fileName={`AN-${shipment.file_no || id}.pdf`} />
      <div ref={contentRef} style={{ backgroundColor: 'white', maxWidth: '900px', margin: '20px auto', padding: '40px', boxShadow: '0 0 10px rgba(0,0,0,0.1)', fontFamily: 'Arial, sans-serif', fontSize: '11px', lineHeight: '1.4' }}>
        {/* Header */}
        <div style={{ marginBottom: '20px', borderBottom: '2px solid #000', paddingBottom: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
            {COMPANY_INFO.logo && <img src={COMPANY_INFO.logo} alt="" style={{ height: '52px', marginRight: '12px' }} />}
            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{COMPANY_INFO.name}</div>
          </div>
          <div style={{ fontSize: '10px', color: '#666' }}>
            <div>{COMPANY_INFO.address}</div>
            <div>{COMPANY_INFO.cityStateZip}</div>
            <div>Tel: {COMPANY_INFO.tel} | Email: {COMPANY_INFO.email}</div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', letterSpacing: '2px' }}>ARRIVAL NOTICE</div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>Date: {currentDate}</div>
        </div>

        {/* Two Column Layout */}
        <div style={{ marginBottom: '25px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <div style={{ marginBottom: '15px' }}>
              <div style={{ fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '5px', marginBottom: '5px' }}>CONSIGNEE</div>
              <div style={{ fontSize: '10px', lineHeight: '1.6' }}>
                <div style={{ fontWeight: 'bold' }}>{shipment.consignee || 'N/A'}</div>
                <div>{getTPAddress(consigneeInfo)}</div>
              </div>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <div style={{ fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '5px', marginBottom: '5px' }}>SHIPPER</div>
              <div style={{ fontSize: '10px', lineHeight: '1.6' }}>
                <div style={{ fontWeight: 'bold' }}>{shipment.shipper || 'N/A'}</div>
                <div>{getTPAddress(shipperInfo)}</div>
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '5px', marginBottom: '5px' }}>NOTIFY PARTY / CUSTOMER</div>
              <div style={{ fontSize: '10px', lineHeight: '1.6' }}>
                <div style={{ fontWeight: 'bold' }}>{shipment.customer || 'N/A'}</div>
                <div>{getTPAddress(customerInfo)}</div>
              </div>
            </div>
          </div>

          <div style={{ borderLeft: '1px solid #ddd', paddingLeft: '15px' }}>
            <table style={{ width: '100%', fontSize: '10px' }}>
              <tbody>
                {[
                  ['FILE NO.', shipment.file_no],
                  [isAir ? 'MAWB NO.' : 'MB/L NO.', shipment.mbl_no],
                  [isAir ? 'HAWB NO.' : 'HB/L NO.', shipment.hbl_no],
                  [isAir ? 'FLIGHT NO.' : 'VESSEL / FLIGHT', shipment.vessel_flight_no],
                  [isAir ? 'DEP. AIRPORT' : 'PORT OF LOADING', shipment.port_of_loading],
                  [isAir ? 'DEST. AIRPORT' : 'PORT OF DISCHARGE', shipment.port_of_discharge],
                  ['ETD', formatDate(shipment.etd)],
                  ['ETA', formatDate(shipment.eta)],
                  ['FINAL DESTINATION', shipment.final_destination],
                  ['FINAL ETA', formatDate(shipment.final_eta)],
                ].map(([label, val]) => (
                  <tr key={label}>
                    <td style={{ fontWeight: 'bold', paddingBottom: '5px', whiteSpace: 'nowrap', paddingRight: '10px' }}>{label}</td>
                    <td style={{ paddingBottom: '5px' }}>{val || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Container / Cargo Table */}
        <div style={{ marginBottom: '25px' }}>
          <div style={{ fontWeight: 'bold', borderBottom: '2px solid #000', paddingBottom: '8px', marginBottom: '10px' }}>SHIPMENT DETAILS</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0', borderBottom: '1px solid #000' }}>
                {['CONTAINER NO.', 'SIZE/TYPE', 'SEAL NO.', 'PKG COUNT', 'PKG TYPE', 'WEIGHT (KG)', 'CBM'].map(h => (
                  <th key={h} style={{ border: '1px solid #000', padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {containers.length > 0 ? containers.map((c, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #000' }}>
                  <td style={{ border: '1px solid #000', padding: '8px' }}>{c.container_no || ''}</td>
                  <td style={{ border: '1px solid #000', padding: '8px' }}>{c.tp_sz || ''}</td>
                  <td style={{ border: '1px solid #000', padding: '8px' }}>{c.seal_no || ''}</td>
                  <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{c.pkg_count || ''}</td>
                  <td style={{ border: '1px solid #000', padding: '8px' }}>{c.pkg_type || ''}</td>
                  <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{c.weight_kg || ''}</td>
                  <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{c.measurement_cbm || ''}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', color: '#999' }}>
                    {shipment.package_count ? `${shipment.package_count} ${shipment.package_type || 'PKG(S)'} — ${shipment.weight_kg || ''} KG` : 'No container details'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Remarks */}
        <div style={{ marginBottom: '25px' }}>
          <div style={{ fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '5px', marginBottom: '10px' }}>REMARKS / MEMO</div>
          <div style={{ fontSize: '10px', minHeight: '60px', padding: '10px', backgroundColor: '#fafafa', border: '1px solid #ddd', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
            {shipment.memo || 'No special instructions'}
          </div>
        </div>

        {/* Footer */}
        <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #000' }}>
          <div style={{ fontSize: '10px', marginBottom: '15px', fontWeight: 'bold', textAlign: 'center' }}>
            Please arrange customs clearance and delivery of the above shipment.
          </div>
          <div style={{ fontSize: '9px', color: '#666', lineHeight: '1.6' }}>
            <p style={{ margin: '5px 0' }}><strong>STANDARD ARRIVAL NOTICE TERMS:</strong></p>
            <p style={{ margin: '5px 0' }}>This Arrival Notice is issued for information only and does not constitute a contract. The consignee is requested to accept delivery within the prescribed free time.</p>
          </div>
        </div>

        <div style={{ marginTop: '40px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ height: '60px' }}></div>
            <div style={{ borderTop: '1px solid #000', paddingTop: '5px', fontSize: '10px', fontWeight: 'bold' }}>Authorized By</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ height: '60px' }}></div>
            <div style={{ borderTop: '1px solid #000', paddingTop: '5px', fontSize: '10px', fontWeight: 'bold' }}>Date</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArrivalNotice;
